import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../style.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Button, Grid } from "@mui/material";
import { OutlinedInput, Stack, styled, Typography } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../layouts/Page";
import NavbarMenu from "../../../components/common/NavbarMenu";
import { sendPasswordResetEmail } from "../../../apiservices/authentication";
import forgotPasswordImg from "../../../assets/images/login/loginImg.png";

const schema = yup
  .object({
    email: yup
      .string()
      .email("*Invalid email format")
      .required("*Email is required"),
  })
  .required();

const FormBox = styled("div")({
  background: "white",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  minHeight: "400px",
  padding: "40px",
});

const RootStyle = styled("div")({
  overflowY: "hidden",
  background: "#04B2AB",
  minHeight: "88.5vh",
  display: "flex",
  alignItems: "center",
  padding: "0px 5%",
});

const TypoTitle = styled(Typography)({
  marginTop: 15,
  color: "#A7A7A7",
});

const TypoMain = styled(Typography)({
  marginTop: 15,
  fontWeight: "bold",
  color: "#21A49E",
});

const StyledButton = styled(Button)({
  height: "40px",
  width: "228px",
  borderRadius: "4px",
  background: "#21A49E",
  color: "#ffffff",
  textDecorationLine: "none",
  marginTop: "20px",
  "&:hover": {
    backgroundColor: "#1B8C87",
    color: "#ffffff",
  },
  "&:active": {
    backgroundColor: "#146F6A",
    color: "#ffffff",
  },
});

export const BackButton = styled(Button)({
  textTransform: "capitalize",
  color: "#21A49E !important",
  fontWeight: "500",
  fontSize: "15px",
  marginTop: "15px",
  "&:hover": {
    color: "#21A49E",
  },
  "&:active": {
    color: "#21A49E",
  },
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const emailData = {
        to: data.email,
        from: "info@infini8ai.com",
        user: data.email,
        link: `${window.location.origin}/reset-password`,
      };

      const response = await sendPasswordResetEmail(emailData);

      if (response.data.success) {
        toast.success("Password reset link sent to your email!");
        setTimeout(() => {
          navigate("/sign-in");
        }, 2000);
      } else {
        toast.error("Failed to send reset email. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => resetField("email");

  return (
    <PageLayout>
      <NavbarMenu />
      <RootStyle>
        <Grid container spacing={2} alignItems="center">
          <Grid item md={4} lg={6} display={{ xs: "none", md: "block" }}>
            <Stack alignItems="center" justifyContent="center">
              <img src={forgotPasswordImg} alt="Forgot Password" />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <FormBox sx={{ width: { xs: "350px", md: "500px" } }}>
              <TypoTitle>Forgot Your Password?</TypoTitle>
              <TypoMain variant="h6">
                Enter your email to reset your password
              </TypoMain>

              <Box sx={{ mt: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <FormControl
                    fullWidth
                    sx={{ m: 1, width: { sm: 300, md: 400 } }}
                    variant="outlined"
                  >
                    <InputLabel htmlFor="outlined-adornment-Email">
                      Email
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-Email"
                      type="text"
                      {...register("email")}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton onClick={handleClick} edge="end">
                            <CancelIcon color="disabled" fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Email"
                    />
                  </FormControl>
                  <Typography sx={{ justifyContent: "center", color: "red" }}>
                    {errors.email?.message}
                  </Typography>
                </Box>
              </Box>

              <StyledButton
                onClick={handleSubmit(onSubmit)}
                type="submit"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </StyledButton>

              <Box
                sx={{
                  borderTop: 1,
                  width: "300px",
                  marginTop: "30px",
                  color: "#D2D2D2",
                }}
              />

              <BackButton onClick={() => navigate("/sign-in")}>
                Back to Login
              </BackButton>
            </FormBox>
          </Grid>
        </Grid>
      </RootStyle>
    </PageLayout>
  );
};

export default ForgotPassword;
